import { Dialog, PlainButton, Tab } from "@jugl-web/ui-components";
import {
  AgentTool,
  ContactPackField,
  type ContactPackParams,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { Button } from "@jugl-web/ui-kit";
import { DiscardDialog } from "@jugl-web/ui-kit/overlay/DiscardDialog";
import isEqual from "lodash/isEqual";
import React, { useEffect, useMemo, useState } from "react";
import { SetFieldsTab } from "./components/SetFieldsTab";
import { HowItWorksTab } from "./components/HowItWorksTab";
import {
  CONTACT_PACK_TOOLS,
  RESERVED_VARIABLE_NAMES,
  toVariableName,
} from "./utils";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as LeadsIcon } from "./assets/leads.svg";
import { ReactComponent as ArrowIcon } from "../../assets/arrow.svg";

type ActiveTab = "fields" | "how-it-works";

const generateId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const extractContactFields = (
  params: AgentTool["params"]
): ContactPackField[] | null => {
  const candidate = (params as Partial<ContactPackParams> | undefined)?.fields;
  return Array.isArray(candidate) ? (candidate as ContactPackField[]) : null;
};

const normalizeFieldsForComparison = (fieldsToNormalize: ContactPackField[]) =>
  fieldsToNormalize.map((field) => ({
    title: field.title.trim(),
    type: field.type,
    required: field.required,
  }));

export interface CollectLeadsSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
}

export const CollectLeadsSettingsDialog: React.FC<
  CollectLeadsSettingsDialogProps
> = ({ isOpen, onClose, entityId }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const { data } = integrationsApi.useGetAiAgentConfigQuery({ entityId });
  const [updateAgentConfig] = integrationsApi.useUpdateAgentConfigMutation();

  const [activeTab, setActiveTab] = useState<ActiveTab>("fields");
  const [fields, setFields] = useState<ContactPackField[]>([]);
  const [fieldIds, setFieldIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const discardChangesConfirmationDialogState = useConfirmationDialogState();

  const existingContactTool = useMemo(
    () => data?.config.tools.find((tool) => tool.pack === "contact"),
    [data]
  );

  const initialFields = useMemo<ContactPackField[]>(
    () =>
      (existingContactTool
        ? extractContactFields(existingContactTool.params)
        : null) ?? [],
    [existingContactTool]
  );

  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("fields");
    setFields(initialFields);
    setFieldIds(initialFields.map(() => generateId()));
  }, [isOpen, initialFields]);

  const isDirty = useMemo(
    () =>
      !isEqual(
        normalizeFieldsForComparison(fields),
        normalizeFieldsForComparison(initialFields)
      ),
    [fields, initialFields]
  );

  const handleRequestClose = () => {
    if (isSaving) {
      return;
    }

    if (isDirty) {
      discardChangesConfirmationDialogState.open({
        onConfirm: onClose,
      });
      return;
    }

    onClose();
  };

  const showSomethingWentWrongToast = () => {
    toast(
      t({
        id: "feedback.something-went-wrong",
        defaultMessage: "Something went wrong",
      }),
      { variant: "error" }
    );
  };

  const handleSave = async () => {
    if (!data) return;

    const normalizedFields = fields.map<ContactPackField>((field) => {
      const trimmedTitle = field.title.trim();
      return {
        ...field,
        title: trimmedTitle,
        variable_name: toVariableName(trimmedTitle),
      };
    });

    if (normalizedFields.some((field) => !field.title)) {
      toast(
        t({
          id: "cx-ai-agents-page.collect-leads-empty-label-error",
          defaultMessage: "Please fill in all field labels",
        }),
        { variant: "error" }
      );
      return;
    }

    if (normalizedFields.some((field) => !field.variable_name)) {
      toast(
        t({
          id: "cx-ai-agents-page.collect-leads-invalid-label-error",
          defaultMessage:
            "Field labels must contain at least one letter or digit",
        }),
        { variant: "error" }
      );
      return;
    }

    const reservedVariableNames = new Set<string>(RESERVED_VARIABLE_NAMES);
    const variableNames = normalizedFields.map((field) => field.variable_name);
    const hasDuplicate =
      new Set(variableNames).size !== variableNames.length ||
      variableNames.some((name) => reservedVariableNames.has(name));
    if (hasDuplicate) {
      toast(
        t({
          id: "cx-ai-agents-page.collect-leads-duplicate-field-error",
          defaultMessage: "Field labels must be unique",
        }),
        { variant: "error" }
      );
      return;
    }

    const otherTools = data.config.tools.filter(
      (tool) => tool.pack !== "contact"
    );

    let nextTools: AgentTool[];
    if (normalizedFields.length === 0) {
      nextTools = otherTools;
    } else {
      const nextContactTool: AgentTool = {
        pack: "contact",
        tools: CONTACT_PACK_TOOLS,
        params: { fields: normalizedFields },
      };
      nextTools = [...otherTools, nextContactTool];
    }

    setIsSaving(true);
    try {
      await updateAgentConfig({
        entityId,
        config: {
          ...data.config,
          tools: nextTools,
        },
      }).unwrap();
      toast(
        t({
          id: "cx-ai-agents-page.collect-leads-success",
          defaultMessage: "Collect Leads automation was updated",
        })
      );
      onClose();
    } catch {
      showSomethingWentWrongToast();
    } finally {
      setIsSaving(false);
    }
  };

  const tabs: { id: ActiveTab; label: string }[] = [
    {
      id: "fields",
      label: t({
        id: "cx-ai-agents-page.collect-leads-tab-set-fields",
        defaultMessage: "Set Fields",
      }),
    },
    {
      id: "how-it-works",
      label: t({
        id: "cx-ai-agents-page.collect-leads-tab-how-it-works",
        defaultMessage: "How it works",
      }),
    },
  ];

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={handleRequestClose}
        className="flex max-h-[820px] w-full max-w-[780px] flex-col overflow-hidden rounded-2xl"
      >
        <div className="border-dark-100 flex h-[60px] shrink-0 items-center justify-between gap-6 border-0 border-b border-solid px-6">
          <div className="flex items-center gap-3">
            <LeadsIcon className="h-[18px] w-[18px]" />
            <span className="font-secondary font-semibold text-[#383838]">
              {t({
                id: "cx-ai-agents-page.collect-leads-dialog-title",
                defaultMessage: "Collect Leads",
              })}
            </span>
          </div>
          <PlainButton
            onClick={handleRequestClose}
            disabled={isSaving}
            className="hover:bg-grey-100 flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
          >
            <CloseIcon />
          </PlainButton>
        </div>
        <div className="border-grey-300 flex shrink-0 items-end gap-1 border-0 border-b border-solid bg-[#EEF2F5] px-6 pt-2">
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              title={tab.label}
              isSelected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              shouldScrollIntoViewOnSelect={false}
              labelClassName="text-sm"
              className="w-auto"
            />
          ))}
        </div>
        <div className="jugl__custom-scrollbar flex max-h-[480px] flex-col overflow-y-auto px-8 py-8">
          {activeTab === "fields" ? (
            <SetFieldsTab
              fields={fields}
              fieldIds={fieldIds}
              setFields={setFields}
              setFieldIds={setFieldIds}
            />
          ) : (
            <HowItWorksTab />
          )}
        </div>
        <div className="border-dark-100 flex shrink-0 items-center justify-end gap-4 border-0 border-t border-solid px-6 py-4">
          <Button
            variant="tertiary"
            onClick={handleRequestClose}
            isDisabled={isSaving}
            className="h-10 w-[120px]"
          >
            {t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            })}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving}
            iconEnd={<ArrowIcon />}
            className="h-10 w-[300px]"
          >
            {t({
              id: "common.save",
              defaultMessage: "Save",
            })}
          </Button>
        </div>
      </Dialog>
      <DiscardDialog
        isOpen={discardChangesConfirmationDialogState.isOpen}
        onCancel={discardChangesConfirmationDialogState.close}
        onDiscard={() =>
          discardChangesConfirmationDialogState.confirm({
            closeOnceConfirmed: true,
          })
        }
      />
    </>
  );
};
